import { supabase } from '../lib/supabase';

// Sample service categories for fallback
const SAMPLE_CATEGORIES = [
  {
    id: 1,
    name: 'Maintenance & Repairs',
    description: 'Keep your golf cart running smoothly',
    icon: 'FaTools',
    display_order: 1,
    is_active: true
  },
  {
    id: 2,
    name: 'Battery Services',
    description: 'Battery testing, replacement, and maintenance',
    icon: 'FaBatteryFull',
    display_order: 2,
    is_active: true
  },
  {
    id: 3,
    name: 'Performance Upgrades',
    description: 'Enhance speed and efficiency',
    icon: 'FaTachometerAlt',
    display_order: 3,
    is_active: true
  },
  {
    id: 4,
    name: 'Custom Work',
    description: 'Personalize your golf cart',
    icon: 'FaPaintBrush',
    display_order: 4,
    is_active: true
  }
];

// Sample services for fallback
const SAMPLE_SERVICES = [
  {
    id: 1,
    category_id: 1,
    name: 'Annual Service Package',
    description: 'Complete annual maintenance including oil change, filter replacement, brake inspection, and battery service',
    price: 189,
    price_type: 'fixed',
    features: ['Oil & filter change', 'Brake inspection', 'Battery test', 'Tire rotation', '25-point inspection'],
    is_popular: true,
    is_active: true
  },
  {
    id: 2,
    category_id: 2,
    name: 'Battery Replacement',
    description: 'Premium deep-cycle battery replacement with 2-year warranty',
    price: 599,
    price_type: 'starting',
    features: ['Premium batteries', '2-year warranty', 'Free installation', 'Old battery disposal'],
    is_popular: true,
    is_active: true
  },
  {
    id: 3,
    category_id: 3,
    name: 'Speed Controller Upgrade',
    description: 'Increase your cart speed safely with our performance controller',
    price: 399,
    price_type: 'fixed',
    features: ['Up to 25% speed increase', 'Safe installation', 'Warranty included'],
    is_popular: true,
    is_active: true
  },
  {
    id: 4,
    category_id: 1,
    name: 'Brake Service',
    description: 'Complete brake inspection and service',
    price: 149,
    price_type: 'starting',
    features: ['Pad replacement', 'Fluid check', 'Adjustment'],
    is_active: true
  },
  {
    id: 5,
    category_id: 4,
    name: 'Custom Paint Job',
    description: 'Professional paint service with color of your choice',
    price: 899,
    price_type: 'starting',
    features: ['Any color available', 'Professional finish', 'Weather resistant'],
    is_active: true
  },
  {
    id: 6,
    category_id: 1,
    name: 'Tire Replacement',
    description: 'New tire installation with alignment',
    price: 299,
    price_type: 'starting',
    features: ['Quality tires', 'Professional installation', 'Alignment included'],
    is_active: true
  }
];

class GolfCartServiceAPI {
  // Service Categories
  async getCategories(includeInactive = false) {
    try {
      let query = supabase
        .from('golf_cart_service_categories')
        .select('*')
        .order('display_order', { ascending: true });

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      if (error) {
        console.error('Database error, using sample categories:', error);
        // Return sample data on error
        return { data: SAMPLE_CATEGORIES, error: null };
      }
      
      // If no data, return sample categories
      if (!data || data.length === 0) {
        return { data: SAMPLE_CATEGORIES, error: null };
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching service categories:', error);
      // Return sample data on error
      return { data: SAMPLE_CATEGORIES, error: null };
    }
  }

  async createCategory(category) {
    try {
      const { data, error } = await supabase
        .from('golf_cart_service_categories')
        .insert([category])
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating category:', error);
      return { data: null, error };
    }
  }

  async updateCategory(id, updates) {
    try {
      const { data, error } = await supabase
        .from('golf_cart_service_categories')
        .update({ ...updates, updated_at: new Date() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating category:', error);
      return { data: null, error };
    }
  }

  async deleteCategory(id) {
    try {
      const { error } = await supabase
        .from('golf_cart_service_categories')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting category:', error);
      return { error };
    }
  }

  // Services
  async getServices(includeInactive = false) {
    try {
      let query = supabase
        .from('golf_cart_services')
        .select(`
          *,
          category:golf_cart_service_categories(*)
        `)
        .order('display_order', { ascending: true });

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      if (error) {
        console.error('Database error, using sample services:', error);
        // Return sample data on error
        return { data: SAMPLE_SERVICES, error: null };
      }
      
      // If no data, return sample services
      if (!data || data.length === 0) {
        return { data: SAMPLE_SERVICES, error: null };
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching services:', error);
      // Return sample data on error
      return { data: SAMPLE_SERVICES, error: null };
    }
  }

  async getServicesByCategory(categoryId, includeInactive = false) {
    try {
      let query = supabase
        .from('golf_cart_services')
        .select('*')
        .eq('category_id', categoryId)
        .order('display_order', { ascending: true });

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching services by category:', error);
      return { data: null, error };
    }
  }

  async getService(id) {
    try {
      const { data, error } = await supabase
        .from('golf_cart_services')
        .select(`
          *,
          category:golf_cart_service_categories(*)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching service:', error);
      return { data: null, error };
    }
  }

  async createService(service) {
    try {
      const { data, error } = await supabase
        .from('golf_cart_services')
        .insert([service])
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating service:', error);
      return { data: null, error };
    }
  }

  async updateService(id, updates) {
    try {
      const { data, error } = await supabase
        .from('golf_cart_services')
        .update({ ...updates, updated_at: new Date() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating service:', error);
      return { data: null, error };
    }
  }

  async deleteService(id) {
    try {
      const { error } = await supabase
        .from('golf_cart_services')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting service:', error);
      return { error };
    }
  }

  // Appointments
  async getAppointments(status = null) {
    try {
      let query = supabase
        .from('golf_cart_service_appointments')
        .select(`
          *,
          service:golf_cart_services(
            *,
            category:golf_cart_service_categories(*)
          )
        `)
        .order('preferred_date', { ascending: true });

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching appointments:', error);
      return { data: null, error };
    }
  }

  async createAppointment(appointment) {
    try {
      const { data, error } = await supabase
        .from('golf_cart_service_appointments')
        .insert([appointment])
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating appointment:', error);
      return { data: null, error };
    }
  }

  async updateAppointment(id, updates) {
    try {
      const { data, error } = await supabase
        .from('golf_cart_service_appointments')
        .update({ ...updates, updated_at: new Date() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating appointment:', error);
      return { data: null, error };
    }
  }

  async deleteAppointment(id) {
    try {
      const { error } = await supabase
        .from('golf_cart_service_appointments')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting appointment:', error);
      return { error };
    }
  }

  // Reorder services
  async reorderServices(serviceOrders) {
    try {
      const updates = serviceOrders.map(({ id, display_order }) => 
        supabase
          .from('golf_cart_services')
          .update({ display_order, updated_at: new Date() })
          .eq('id', id)
      );

      const results = await Promise.all(updates);
      const hasError = results.some(result => result.error);
      
      if (hasError) {
        throw new Error('Failed to reorder services');
      }
      
      return { error: null };
    } catch (error) {
      console.error('Error reordering services:', error);
      return { error };
    }
  }

  // Reorder categories
  async reorderCategories(categoryOrders) {
    try {
      const updates = categoryOrders.map(({ id, display_order }) => 
        supabase
          .from('golf_cart_service_categories')
          .update({ display_order, updated_at: new Date() })
          .eq('id', id)
      );

      const results = await Promise.all(updates);
      const hasError = results.some(result => result.error);
      
      if (hasError) {
        throw new Error('Failed to reorder categories');
      }
      
      return { error: null };
    } catch (error) {
      console.error('Error reordering categories:', error);
      return { error };
    }
  }
}

export const golfCartServiceAPI = new GolfCartServiceAPI();
